import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warehouse as WarehouseIcon,
  Print as PrintIcon,
  FileDownload as ExcelIcon
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLanguage } from '../contexts/LanguageContext';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import config from '../config';
import WarehouseDialog from '../components/warehouses/WarehouseDialog';
import { Warehouse } from '../types/warehouses.types';
import { warehousesService } from '../services/warehouses.service';
import { useConfirm } from '../hooks/useConfirm';
import { toast } from 'react-toastify';

const translations = {
  en: {
    title: 'Warehouses',
    addWarehouse: 'Add Warehouse',
    warehouseCode: 'Code',
    warehouseName: 'Name',
    warehouseNameAr: 'Name (AR)',
    warehouseType: 'Type',
    inventoryAccount: 'Inventory Account',
    status: 'Status',
    actions: 'Actions',
    search: 'Search by code or name...',
    noData: 'No warehouses found',
    confirmDelete: 'Are you sure you want to delete this warehouse?',
    active: 'Active',
    inactive: 'Inactive',
    typeMain: 'Main',
    typeBranch: 'Branch',
    typeTransit: 'Transit',
    totalWarehouses: 'Total Warehouses',
    activeWarehouses: 'Active',
    refresh: 'Refresh',
    rowsPerPage: 'Rows per page:',
    edit: 'Edit',
    delete: 'Delete'
  },
  ar: {
    title: 'المخازن',
    addWarehouse: 'إضافة مخزن',
    warehouseCode: 'الرمز',
    warehouseName: 'الاسم',
    warehouseNameAr: 'الاسم (عربي)',
    warehouseType: 'النوع',
    inventoryAccount: 'حساب المخزون',
    status: 'الحالة',
    actions: 'الإجراءات',
    search: 'البحث بالرمز أو الاسم...',
    noData: 'لا توجد مخازن',
    confirmDelete: 'هل أنت متأكد من حذف هذا المخزن؟',
    active: 'نشط',
    inactive: 'غير نشط',
    typeMain: 'رئيسي',
    typeBranch: 'فرع',
    typeTransit: 'عبور',
    totalWarehouses: 'إجمالي المخازن',
    activeWarehouses: 'نشط',
    refresh: 'تحديث',
    rowsPerPage: 'عدد الصفوف:',
    edit: 'تعديل',
    delete: 'حذف'
  },
};

const WarehousesPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | undefined>();

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0
  });

  useEffect(() => {
    loadWarehouses();
  }, []);

  useEffect(() => {
    filterWarehouses();
    calculateStats();
  }, [searchTerm, warehouses]);

  const loadWarehouses = async () => {
    setLoading(true);
    try {
      const data = await warehousesService.getAll();
      setWarehouses(data);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterWarehouses = () => {
    if (!searchTerm.trim()) {
      setFilteredWarehouses(warehouses);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = warehouses.filter(
      (warehouse) =>
        warehouse.warehouseCode.toLowerCase().includes(term) ||
        warehouse.warehouseName.toLowerCase().includes(term) ||
        warehouse.warehouseNameAr.toLowerCase().includes(term)
    );
    setFilteredWarehouses(filtered);
    setPage(0);
  };

  const calculateStats = () => {
    const total = warehouses.length;
    const active = warehouses.filter(w => w.isActive).length;
    setStats({ total, active });
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      // Company Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          worksheet.addImage(logoImage, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 60 } });
        } catch {}
      }

      // Company Header
      worksheet.mergeCells('A1:D1');
      const companyCell = worksheet.getCell('A1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 16, bold: true, color: { argb: 'FF5E35B1' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 50;

      // Report Title
      worksheet.mergeCells('A3:D3');
      const titleCell = worksheet.getCell('A3');
      titleCell.value = t.title;
      titleCell.font = { size: 13, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5E35B1' } };
      worksheet.getRow(3).height = 28;

      // Date row
      worksheet.mergeCells('A4:D4');
      const dateCell = worksheet.getCell('A4');
      dateCell.value = `${isRTL ? 'تاريخ التصدير' : 'Exported'}: ${new Date().toLocaleDateString('en-GB')} | ${isRTL ? 'الإجمالي' : 'Total'}: ${filteredWarehouses.length}`;
      dateCell.font = { size: 9, color: { argb: 'FF666666' } };
      dateCell.alignment = { horizontal: 'center' };

      // Headers row 6
      const headers = [t.warehouseCode, t.warehouseName, t.warehouseType, t.status];
      const headerRow = worksheet.getRow(6);
      headerRow.values = headers;
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 24;

      // Data rows
      let currentRow = 7;
      filteredWarehouses.forEach((w, idx) => {
        const row = worksheet.getRow(currentRow);
        row.values = [
          w.warehouseCode,
          isRTL ? w.warehouseNameAr : w.warehouseName,
          getWarehouseTypeLabel(w.warehouseType),
          w.isActive ? t.active : t.inactive,
        ];
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        row.height = 20;
        if (idx % 2 === 0) {
          row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
        }
        currentRow++;
      });

      // Column widths
      worksheet.getColumn(1).width = 14;
      worksheet.getColumn(2).width = 30;
      worksheet.getColumn(3).width = 16;
      worksheet.getColumn(4).width = 14;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Warehouses_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Excel exported successfully');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(isRTL ? 'فشل التصدير' : 'Excel export failed');
    }
  };

  const handlePrintList = () => {
    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#1976d2;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const rows = filteredWarehouses.map(w => `
      <tr>
        <td>${w.warehouseCode}</td>
        <td>${isRTL ? w.warehouseNameAr : w.warehouseName}</td>
        <td>${isRTL ? w.warehouseNameAr : w.warehouseName}</td>
        <td>${getWarehouseTypeLabel(w.warehouseType)}</td>
        <td>${w.isActive ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive')}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head>
      <style>
        body{font-family:${isRTL ? 'Cairo,' : ''}Arial,sans-serif;margin:20px;font-size:12px;color:#333;}
        .header{text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;}
        .header h2{color:#1976d2;margin:5px 0;}
        .header p{font-size:11px;color:#666;margin:0;}
        h1{text-align:center;color:#1976d2;margin:10px 0 15px;font-size:18px;}
        table{width:100%;border-collapse:collapse;margin-top:10px;}
        th,td{border:1px solid #ddd;padding:6px 8px;font-size:11px;}
        th{background:#1976d2;color:white;font-weight:bold;}
        tr:nth-child(even){background:#f9f9f9;}
        @page{size:A4 landscape;margin:1cm;}
      </style></head><body>
      ${companyHeader}
      <h1>${t.title}</h1>
      <table>
        <thead><tr>
          <th>${t.warehouseCode}</th>
          <th>${t.warehouseName}</th>
          <th>${t.warehouseNameAr}</th>
          <th>${t.warehouseType}</th>
          <th>${t.status}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="margin-top:10px;font-size:11px;color:#999;">Printed: ${new Date().toLocaleString()}</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };

  const handleAdd = () => {
    setSelectedWarehouse(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (warehouse: Warehouse) => {
    setSelectedWarehouse(warehouse);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) return;
    try {
      await warehousesService.delete(id);
      loadWarehouses();
    } catch (error) {
      toast.error(isRTL ? 'فشل حذف المخزن' : 'Failed to delete warehouse');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedWarehouse(undefined);
  };

  const handleSave = () => {
    loadWarehouses();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getWarehouseTypeLabel = (type?: string) => {
    if (!type) return '-';
    switch (type) {
      case 'Main': return t.typeMain;
      case 'Branch': return t.typeBranch;
      case 'Transit': return t.typeTransit;
      default: return type;
    }
  };

  const paginatedWarehouses = filteredWarehouses.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
                  <Typography variant="body2">{t.totalWarehouses}</Typography>
                </Box>
                <StorageIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
          <Card elevation={4} sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{stats.active}</Typography>
                  <Typography variant="body2">{t.activeWarehouses}</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 60, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Header & Actions */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarehouseIcon sx={{ fontSize: 32, color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={isRTL ? 'تصدير Excel' : 'Export Excel'}>
              <IconButton
                color="success"
                onClick={exportToExcel}
                sx={{ bgcolor: 'rgba(46, 125, 50, 0.1)', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.2)' } }}
              >
                <ExcelIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isRTL ? 'طباعة القائمة' : 'Print List'}>
              <IconButton
                color="primary"
                onClick={handlePrintList}
                sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t.refresh}>
              <IconButton
                color="primary"
                onClick={loadWarehouses}
                sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
                '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' }
              }}
            >
              {t.addWarehouse}
            </Button>
          </Stack>
        </Box>

        <TextField
          fullWidth
          placeholder={t.search}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: '#667eea' }} />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseCode}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseNameAr}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseType}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.inventoryAccount}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedWarehouses.map((warehouse) => (
                <TableRow key={warehouse.warehouseId} hover sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.05)' } }}>
                  <TableCell>
                    <Typography fontWeight="bold" color="#667eea">{warehouse.warehouseCode}</Typography>
                  </TableCell>
                  <TableCell>{warehouse.warehouseName}</TableCell>
                  <TableCell>{warehouse.warehouseNameAr}</TableCell>
                  <TableCell>{getWarehouseTypeLabel(warehouse.warehouseType)}</TableCell>
                  <TableCell>
                    {warehouse.inventoryAccountCode ? (
                      <>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {warehouse.inventoryAccountCode}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isRTL ? warehouse.inventoryAccountName : warehouse.inventoryAccountName}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" component="div" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={warehouse.isActive ? t.active : t.inactive}
                      color={warehouse.isActive ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title={t.edit}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(warehouse)}
                          sx={{ color: 'primary.main', '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t.delete}>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(warehouse.warehouseId)}
                          sx={{ color: 'error.main', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedWarehouses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={filteredWarehouses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t.rowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            isRTL ? `${from}-${to} من ${count}` : `${from}-${to} of ${count}`
          }
        />
      </TableContainer>

      <WarehouseDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        warehouse={selectedWarehouse}
        onSave={handleSave}
      />
    </Box>
  );
};

export default WarehousesPage;